import {useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/**
 * Master kill-switch for the deep-drawer lazy-load fallback. Flip to `false`
 * to disable the runtime fetch entirely (e.g. once Optimizely Graph's
 * MaxExpansionDepth is raised on the tenant and re-indexing is complete).
 * When disabled, the hook is a no-op and drawers render whatever the page
 * query provided — items with `Expanded === null` will simply be skipped by
 * `renderArea` in index.jsx.
 */
const LAZY_LOAD_ENABLED = true

/**
 * Fallback hook for the Optimizely Graph max-expansion-depth cap.
 *
 * The page-level query recurses through Expanded { ...IContentFragment } up to
 * Graph's configured MaxExpansionDepth. When a GDIDrawerBlock sits deeper than
 * that cap, two things go wrong in the page payload:
 *   1. Some / all entries in the drawer's content areas (title / body / footer)
 *      arrive with `Expanded === null`.
 *   2. The arrays themselves may even be missing items that authors recently
 *      added in the CMS, because the area projection at that depth is partial.
 *
 * Rather than chasing each child by Id (which only fixes #1 and leaves #2
 * stale), this hook re-fetches the **entire drawer block** by its own
 * `ContentLink.Id`. Querying a single Id as the GraphQL root resets the
 * expansion-depth counter, so the title / body / footer arrays come back
 * complete (any newly added content is included) with each direct child
 * fully expanded up to MaxExpansionDepth from the drawer root.
 *
 * Returns the freshest available copy of each area: the fetched arrays when
 * the request has completed, otherwise the original arrays from the page
 * payload (so the drawer still renders whatever was already available).
 */

const buildQuery = (fragments) => `
query ($id: Int!, $locale: [Locales]) {
    Content(where: { ContentLink: { Id: { eq: $id } } }, locale: $locale) {
        items { ...IContentFragment }
    }
}
${fragments}
`

const useDrawerLazyContent = ({drawerId, titleArea, bodyArea, footerArea, enabled}) => {
    const {locale} = useIntl()
    const [fetched, setFetched] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const fetchedIdRef = useRef(null)
    // Graph's Locales enum uses underscores (e.g. fr_CA) — the app's locale strings use hyphens (e.g. fr-CA).
    const graphLocale = locale?.replace('-', '_')

    useEffect(() => {
        if (!LAZY_LOAD_ENABLED) return undefined
        if (!enabled) return undefined
        if (typeof drawerId !== 'number') return undefined
        // Dedupe: fetch each drawer Id at most once per mounted lifetime.
        if (fetchedIdRef.current === drawerId) return undefined

        const {app: appConfig} = getConfig() || {}
        const gateway = appConfig?.OPTIMIZELY_GRAPH_GATEWAY
        const singleKey = appConfig?.OPTIMIZELY_GRAPH_SINGLE_KEY
        if (!gateway || !singleKey) return undefined

        fetchedIdRef.current = drawerId
        let aborted = false
        setIsLoading(true)
        ;(async () => {
            try {
                const {fragments} = await import('../../../../../../../optimizely/query')
                const response = await fetch(`${gateway}content/v2?auth=${singleKey}`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        query: buildQuery(fragments),
                        variables: {id: drawerId, locale: graphLocale ? [graphLocale] : undefined}
                    })
                })
                if (!response.ok) return
                const json = await response.json()
                if (aborted) return
                const drawer = json?.data?.Content?.items?.[0]
                if (!drawer) return
                setFetched({
                    titleArea: drawer.GDIDrawerBlockTitle,
                    bodyArea: drawer.GDIDrawerBlockContent,
                    footerArea: drawer.Footer
                })
            } catch (e) {
                if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.warn('[GDIDrawerBlock] Lazy drawer fetch failed:', e)
                }
            } finally {
                if (!aborted) setIsLoading(false)
            }
        })()

        return () => {
            aborted = true
        }
    }, [enabled, drawerId, graphLocale])

    return useMemo(
        () => ({
            titleArea: fetched?.titleArea ?? titleArea,
            bodyArea: fetched?.bodyArea ?? bodyArea,
            footerArea: fetched?.footerArea ?? footerArea,
            isLoading
        }),
        [fetched, titleArea, bodyArea, footerArea, isLoading]
    )
}

export default useDrawerLazyContent
